// docs/93 AQ10 — what moving through water leaves on the board: the little V of a wake and its
// trailing ripples behind anything going through shallows or the deep (bigger behind a huge body,
// fading over about a second), foam puffs where a figure goes in or comes out, the splash of a
// shove into the deep, the dive and the rise of a swimmer, and the swirl a water foe leaves as it
// sinks away for good (`Held { Withdrew }`). One per board scene, found by the figures through
// `scene.data` (`waterFxOf`), so every movement — a walk, a shove, a haul — leaves its wake without
// the playback telling it. Wakes live on the ground layer under the figures (over the docs/28
// surface); splashes in the effects layer. Driven from the scene's update, not tweens
// (`setSnapshot` kills every tween). Reduce motion keeps the foam and drops the spray.
import Phaser from 'phaser';
import { TEX, ensureSpellTextures } from '../spells/textures';
import { waterAt, type BoardLike, type Water, type WaterMoment } from '../../core/sim/water';
import type { Tile } from '../../core/sim/types';

type P = { x: number; y: number };

export interface WaterFxHost {
  scene: Phaser.Scene;
  /** On the ground under the figures (wakes, foam rings, swirls). */
  addGround(o: Phaser.GameObjects.GameObject): void;
  /** Over everything on the board (spray). */
  add(o: Phaser.GameObjects.GameObject): void;
  cell: number;
}

export interface WaterQuality { density: number; still: boolean }

/** How long a wake's V and its ripples take to fade. */
export const WAKE_MS = 1100;
const FOAM = 0xeaf6ff;
const SPRAY = [0xffffff, 0xd6eeff, 0x9cc8ec];

interface Wake { at: P; dir: P; size: number; born: number; deep: boolean }
interface Swirl { at: P; size: number; born: number; ms: number }
interface Ring { at: P; size: number; born: number; ms: number; colour: number; alpha: number }

const KEY = 'fkWaterFx';

/** The board scene's water effects, if it has any (UnitSprite looks them up). */
export function waterFxOf(scene: Phaser.Scene): WaterFx | undefined {
  return scene.data?.get(KEY) as WaterFx | undefined;
}

export class WaterFx {
  private board?: BoardLike;
  private wet = false;
  private wakes: Wake[] = [];
  private swirls: Swirl[] = [];
  private rings: Ring[] = [];
  private readonly g: Phaser.GameObjects.Graphics;
  private readonly live = new Set<Phaser.GameObjects.GameObject>();
  private readonly onUpdate = (t: number) => this.update(t);

  constructor(private readonly host: WaterFxHost, private quality: WaterQuality) {
    ensureSpellTextures(host.scene);
    this.g = host.scene.add.graphics();
    host.addGround(this.g);
    host.scene.data.set(KEY, this);
    host.scene.events.on(Phaser.Scenes.Events.UPDATE, this.onUpdate);
    host.scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.destroy());
  }

  setQuality(q: WaterQuality): void { this.quality = q; }

  /** The board the water is read from (a new snapshot; a terrain change). */
  setBoard(board: BoardLike | undefined): void {
    this.board = board;
    this.wet = !!board && board.tiles.some((t, i) => waterAt(board, { x: i % board.w, y: Math.floor(i / board.w) }) !== undefined);
  }

  /** Whether the board holds any water a figure could stand in. */
  get any(): boolean { return this.wet; }

  /** `field::is_water` for a square of the board drawn now. */
  at(t: Tile): Water | undefined { return this.board && this.wet ? waterAt(this.board, t) : undefined; }

  /** A skipped playback or a new board: every wake, swirl and splash goes. */
  clear(): void {
    this.wakes = []; this.swirls = []; this.rings = [];
    this.g.clear();
    for (const o of this.live) o.destroy();
    this.live.clear();
  }

  destroy(): void {
    this.host.scene.events.off(Phaser.Scenes.Events.UPDATE, this.onUpdate);
    this.clear();
    if (this.host.scene.data?.get(KEY) === this) this.host.scene.data.remove(KEY);
  }

  /** A wake behind a figure at `at` (its feet) going `dir` (a unit vector): a V and a ripple ring. */
  wake(at: P, dir: P, size: number, deep: boolean): void {
    if (this.wakes.length > 120) this.wakes.shift();
    this.wakes.push({ at: { ...at }, dir: { ...dir }, size, born: this.host.scene.time.now, deep });
  }

  /** The water answers a figure going in, out, under or up (`waterMoment`) at its feet. */
  moment(kind: WaterMoment, at: P, size: number): void {
    const c = this.host.cell * size;
    switch (kind) {
      case 'enter': this.foam(at, size, 1); this.spray(at, size, 7, 0.8); break;
      case 'leave': this.foam(at, size, 0.8); this.drips(at, size); break;
      case 'plunge': this.foam(at, size, 1.5); this.spray(at, size, 18, 1.6); this.ring(at, size, 700, FOAM, 0.9); this.ring(at, size * 1.4, 1100, FOAM, 0.55); break;
      case 'dive': this.ring(at, size, 900, FOAM, 0.8); this.spray(at, size, 5, 0.5); this.bubbles(at, size, 6); break;
      case 'surface': this.foam(at, size, 1.1); this.spray({ x: at.x, y: at.y - c * 0.1 }, size, 10, 1.1); break;
    }
  }

  /** docs/93 AQ9: a water foe gives up the fight and sinks away for good: a swirl, bubbles, a last ring. */
  swirl(at: P, size: number, ms: number): void {
    this.swirls.push({ at: { ...at }, size, born: this.host.scene.time.now, ms });
    this.ring(at, size, ms, FOAM, 0.7);
    this.bubbles(at, size, 10);
  }

  // ---- parts -------------------------------------------------------------------------------------

  private ring(at: P, size: number, ms: number, colour: number, alpha: number): void {
    this.rings.push({ at: { ...at }, size, born: this.host.scene.time.now, ms, colour, alpha });
  }

  private foam(at: P, size: number, k: number): void {
    this.ring(at, size * 0.8 * k, 650, FOAM, 0.95);
    if (this.quality.still || this.quality.density < 0.15) return;
    const c = this.host.cell * size;
    this.burst(TEX.smoke, at, Math.round(5 * k * this.quality.density) + 2, {
      lifespan: { min: 380, max: 700 }, speedX: { min: -14 * k, max: 14 * k }, speedY: { min: -6, max: 2 }, scale: { start: 0.25 * size, end: 0.6 * size }, alpha: { start: 0.75, end: 0 },
      tint: [FOAM, 0xcfe6f6], emitZone: { type: 'random', source: new Phaser.Geom.Ellipse(0, -1, c * 0.6, c * 0.18) as unknown as Phaser.Types.GameObjects.Particles.RandomZoneSource },
    });
  }

  private spray(at: P, size: number, n: number, k: number): void {
    if (this.quality.still || this.quality.density < 0.15) return;
    this.burst(TEX.drop, at, Math.round(n * this.quality.density) + 2, {
      lifespan: { min: 420, max: 760 }, speed: { min: 18 * k, max: 46 * k * Math.sqrt(size) }, angle: { min: 215, max: 325 }, gravityY: 170,
      scale: { start: 0.9, end: 0.5 }, alpha: { start: 1, end: 0.2 }, color: SPRAY,
    });
  }

  private drips(at: P, size: number): void {
    if (this.quality.still || this.quality.density < 0.15) return;
    const c = this.host.cell * size;
    this.burst(TEX.drop, { x: at.x, y: at.y - c * 0.4 }, Math.round(5 * this.quality.density) + 2, {
      lifespan: 520, speedY: { min: 0, max: 8 }, speedX: { min: -4, max: 4 }, gravityY: 120, scale: { start: 0.7, end: 0.4 }, alpha: { start: 0.9, end: 0 }, color: SPRAY,
      emitZone: { type: 'random', source: new Phaser.Geom.Rectangle(-c * 0.25, -c * 0.3, c * 0.5, c * 0.3) as unknown as Phaser.Types.GameObjects.Particles.RandomZoneSource },
    });
  }

  private bubbles(at: P, size: number, n: number): void {
    if (this.quality.still || this.quality.density < 0.15) return;
    const c = this.host.cell * size;
    this.burst(TEX.dot, at, Math.round(n * this.quality.density) + 1, {
      lifespan: { min: 500, max: 1100 }, speedY: { min: -10, max: -4 }, speedX: { min: -3, max: 3 }, scale: { start: 0.5, end: 0.9 }, alpha: { start: 0.9, end: 0 }, tint: [0xe8f6ff, 0xa8d4f0],
      emitZone: { type: 'random', source: new Phaser.Geom.Ellipse(0, -1, c * 0.5, c * 0.16) as unknown as Phaser.Types.GameObjects.Particles.RandomZoneSource },
    }, true);
  }

  private burst(tex: string, at: P, n: number, cfg: Phaser.Types.GameObjects.Particles.ParticleEmitterConfig, ground = false): void {
    const e = this.host.scene.add.particles(at.x, at.y, tex, { ...cfg, emitting: false });
    (ground ? this.host.addGround : this.host.add).call(this.host, e);
    this.live.add(e);
    e.explode(n, 0, 0);
    this.host.scene.time.delayedCall(1300, () => { this.live.delete(e); e.destroy(); });
  }

  // ---- frame -------------------------------------------------------------------------------------

  private update(now: number): void {
    const g = this.g;
    g.clear();
    if (!this.wakes.length && !this.swirls.length && !this.rings.length) return;
    const cell = this.host.cell;
    this.wakes = this.wakes.filter((w) => now - w.born < WAKE_MS);
    for (const w of this.wakes) {
      const k = (now - w.born) / WAKE_MS;
      const a = (1 - k) * (w.deep ? 0.85 : 0.7);
      const s = cell * Math.max(1, w.size * 0.75);
      // the V: two arms spreading back from where the figure was, longer and wider as it fades
      const back = { x: -w.dir.x, y: -w.dir.y }, side = { x: -w.dir.y, y: w.dir.x };
      const len = s * (0.3 + 0.35 * k), spread = s * (0.12 + 0.3 * k);
      const tip = { x: w.at.x + back.x * s * 0.05, y: w.at.y - 1 + back.y * s * 0.05 };
      g.lineStyle(w.size >= 2 ? 2 : 1, FOAM, a);
      for (const sgn of [1, -1]) {
        g.lineBetween(tip.x, tip.y, tip.x + back.x * len + side.x * spread * sgn, tip.y + (back.y * len + side.y * spread * sgn) * 0.6);
      }
      // a trailing ripple ring where it passed
      const r = s * (0.18 + 0.42 * k);
      g.lineStyle(1, FOAM, a * 0.6).strokeEllipse(w.at.x, w.at.y - 1, r * 2, r * 0.7);
    }
    this.rings = this.rings.filter((r) => now - r.born < r.ms);
    for (const r of this.rings) {
      const k = (now - r.born) / r.ms;
      const rr = cell * r.size * (0.25 + 0.45 * k);
      g.lineStyle(r.size >= 2 ? 2 : 1, r.colour, r.alpha * (1 - k)).strokeEllipse(r.at.x, r.at.y - 1, rr * 2, rr * 0.7);
    }
    this.swirls = this.swirls.filter((s) => now - s.born < s.ms);
    for (const s of this.swirls) {
      const k = (now - s.born) / s.ms;
      const r0 = cell * s.size * 0.42 * (1 - 0.6 * k);
      // three arms of a whirlpool turning in on the spot
      for (let arm = 0; arm < 3; arm++) {
        g.lineStyle(1, FOAM, 0.85 * (1 - k));
        g.beginPath();
        for (let i = 0; i <= 12; i++) {
          const f = i / 12;
          const ang = arm * (Math.PI * 2 / 3) + k * 9 + f * 2.6;
          const rr = r0 * (1 - f * 0.8);
          const x = s.at.x + Math.cos(ang) * rr, y = s.at.y - 1 + Math.sin(ang) * rr * 0.35;
          if (i === 0) g.moveTo(x, y); else g.lineTo(x, y);
        }
        g.strokePath();
      }
    }
  }
}
